import { useState } from 'react';
import Spinner from '../Spinner';
import { Project } from 'axiosApi/models';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import {
	Typography,
	Button,
	Box,
	Card,
  } from '@mui/material';
import { DataGrid, GridColDef, GridInitialState } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import { DEFAULT_PAGINATION, useSearch } from 'hooks/useSearch';
import SearchInput from 'components/SearchInput';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import { useNavigate } from 'react-router';
import { getUserEntity } from 'common/UserEntityProvider';
import { SCOPES } from 'common/permissions';
import intl from 'components/i18n/ReactIntlWrapper';


const Projects = () => {

	const api: Api = useApi();
	const navigate = useNavigate();

	const entityType = getUserEntity();
	
	const [errors, setErrors] = useState({});
	const [searchTerm, setSearchTerm] = useState<string>('');

    const toast = useToastMessageQueue();

	const apiPromise = (
    searchTerm: string,
    pageNum: number,
    pageSize: number,
    sort: string,
    order: string
  	) => {
  	  return api.projectApi.apiVversionProjectSearchGet("1", searchTerm, pageNum, pageSize, sort, order);
  	};

	const processError = (error: any) => {
	handleAPIError(error, toast, errors);
	setErrors({ ...errors });
	};

	const { data, loading, onPaginationModelChange, handleSortModelChange, paginationModel } = useSearch<Project>({
		apiPromise: apiPromise,
		searchTerm: searchTerm,
		defaultSort: { sort: 'Name', order: 'asc' },
		onError: processError,
		pageKey: 'projects',
	});


	const handleClearSearch = () => {
    	setSearchTerm('');
  	};

	const INITIAL_STATE: GridInitialState = {
		pagination: {
		  paginationModel: {
			page: DEFAULT_PAGINATION.pageNum,
			pageSize: DEFAULT_PAGINATION.pageSize,
		  },
		},
		sorting: {
		  sortModel: [
			{
			  field: 'Name',
			  sort: 'asc',
			},
		  ],
		},
	  };


	const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: intl.get('projectsList.table.thead.name'),
      width: 250,
      flex: 1,
    },
    {
      field: 'client',
      headerName: intl.get('projectsList.table.thead.client'),
      width: 250,
      flex: 1,
	  renderCell: (params) => <>{params.row.client?.name || ''}</>,
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      renderCell: (params) => (
        <DefaultActions
          row={params.row}
          entityType={entityType.entityType}
          onEdit={() => navigate(`/project/detail/${params.row.id}`)}
          editScopes={[SCOPES['projects.edit']]}
        />
      ),
    },
  ];

	return (
		<Card sx={{ m: 2 }}>
		<Box sx={{ p: 3 }}>
			<Typography color="primary" variant="h2" sx={{ mb: 2 }}>
				{intl.get('projects.header')}
			</Typography>
			<Box sx={{ p: 3 }}>
				<Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
					<SearchInput value={searchTerm} onChange={setSearchTerm} onClear={handleClearSearch} />
					<Button onClick={() => navigate(`/project/new`)}>
					<AddIcon /> {intl.get('project.new') ? intl.get('project.new') : intl.get('button.new')}
					</Button>
				</Box>
				<Box style={{ width: '100%', display: 'flex' }}>
					{loading && <Spinner />}
					{data && (
					<DataGrid
						rows={data.queryResult || []}
						columns={columns}
						rowCount={data.totalRecords || 0}
						loading={loading}
						paginationModel={paginationModel}
						onPaginationModelChange={onPaginationModelChange}
						onSortModelChange={handleSortModelChange}
					/>
					)}
				</Box>
			</Box>
		</Box>
		</Card>
    );
}

export default Projects;